// src/tenantAdmin/pages/TenantUserListPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Search, UserPlus } from 'lucide-react';
import AdminTable from '../../admin/components/AdminTable.jsx';
import AdminPagination from '../../admin/components/AdminPagination.jsx';
import ConfirmDialog from '../../admin/components/ConfirmDialog.jsx';
import { useAuth } from '../../auth/AuthContext.jsx';
import {
  listTenantUsers,
  deactivateTenantUser,
  changeTenantUserRole,
  removeTenantUser,
} from '../tenantAdminApi.js';
import TenantCreateUserModal from './TenantCreateUserModal.jsx';

const LIMIT = 20;

export default function TenantUserListPage() {
  const { activeTenant, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [emailFilter, setEmailFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [lastWarning, setLastWarning] = useState('');

  const tenantId = activeTenant?.tenant_id;
  const currentUserId = user?.user_id;

  const loadUsers = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError('');
    try {
      const data = await listTenantUsers({ limit: LIMIT, offset, email: emailFilter || undefined });
      setUsers(data.users);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [offset, emailFilter, tenantId]);

  // Reset pagination offset when the tenant or email filter changes to avoid
  // showing a stale offset (e.g. offset=40 after switching to a tenant with 3 users).
  useEffect(() => { setOffset(0); }, [tenantId, emailFilter]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  function handleSearch(e) {
    e.preventDefault();
    setOffset(0);
  }

  async function handleChangeRole(row, newRole) {
    if (newRole === row.role) return;
    try {
      await changeTenantUserRole(row.user_id, newRole);
      await loadUsers();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err.message);
    }
  }

  async function confirmRemove() {
    if (!removeTarget) return;
    // ConfirmDialog owns the error path (await + inline error banner).
    // On success it calls onClose, which clears removeTarget.
    await removeTenantUser(removeTarget.user_id);
    await loadUsers();
  }

  async function handleDeactivate(row) {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Disattivare l'utente ${row.email}?`)) return;
    try {
      await deactivateTenantUser(row.user_id);
      loadUsers();
    } catch (err) {
      if (err.code === 'USER_HAS_OTHER_TENANTS') {
        // eslint-disable-next-line no-alert
        alert("L'utente appartiene anche ad altri tenant. Per disattivarlo, rimuovilo dal tenant. Solo un superadmin può disattivare un utente con più tenant.");
      } else {
        // eslint-disable-next-line no-alert
        alert(err.message);
      }
    }
  }

  if (!activeTenant) return null;

  const columns = [
    { key: 'email', label: 'Email' },
    {
      key: 'is_active', label: 'Stato', width: '100px',
      render: (row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <span style={{
            width: '8px', height: '8px', borderRadius: '50%',
            background: row.is_active ? '#10B981' : '#EF4444',
            display: 'inline-block',
          }} />
          {row.is_active ? 'Attivo' : 'Inattivo'}
        </span>
      ),
    },
    {
      key: 'role', label: 'Ruolo nel tenant', width: '160px',
      render: (row) => {
        const isSelf = row.user_id === currentUserId;
        return (
          <select
            value={row.role || 'editor'}
            disabled={isSelf}
            title={isSelf ? 'Non puoi cambiare il tuo ruolo' : undefined}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => handleChangeRole(row, e.target.value)}
            style={{
              padding: '4px 8px', borderRadius: '6px', fontSize: '12px',
              border: '1px solid rgba(0,0,0,0.1)', background: '#fff',
              fontFamily: "'DM Sans', sans-serif",
              cursor: isSelf ? 'not-allowed' : 'pointer',
            }}
          >
            <option value="viewer">viewer</option>
            <option value="editor">editor</option>
            <option value="admin">admin</option>
          </select>
        );
      },
    },
    {
      key: 'created_at', label: 'Creato il', width: '140px',
      render: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString('it-IT') : '—',
    },
    {
      key: 'actions', label: '', width: '200px',
      render: (row) => {
        const isSelf = row.user_id === currentUserId;
        return (
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
            {row.is_active && (
              <button
                onClick={(e) => { e.stopPropagation(); if (!isSelf) handleDeactivate(row); }}
                disabled={isSelf}
                title={isSelf ? 'Non puoi disattivare te stesso' : undefined}
                style={{
                  padding: '4px 10px', borderRadius: '6px', fontSize: '11px',
                  cursor: isSelf ? 'not-allowed' : 'pointer',
                  border: '1px solid rgba(239,68,68,0.3)',
                  background: 'rgba(239,68,68,0.06)',
                  color: '#EF4444',
                  opacity: isSelf ? 0.5 : 1,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Disattiva
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); if (!isSelf) setRemoveTarget(row); }}
              disabled={isSelf}
              title={isSelf ? 'Non puoi rimuoverti dal tenant' : undefined}
              style={{
                padding: '4px 10px', borderRadius: '6px', fontSize: '11px',
                cursor: isSelf ? 'not-allowed' : 'pointer',
                border: '1px solid rgba(239,68,68,0.3)',
                background: 'rgba(239,68,68,0.06)',
                color: '#EF4444',
                opacity: isSelf ? 0.5 : 1,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              Rimuovi
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '32px 40px', fontFamily: "'DM Sans', sans-serif", color: '#1d1d1f', maxWidth: '1100px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>Gestione utenti</h1>
          <p style={{ color: '#6c6c6d', fontSize: '13px', margin: '4px 0 0' }}>
            Utenti del tenant {activeTenant.display_name}
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '8px 16px', borderRadius: '8px', border: 'none',
            background: '#E8002D', color: '#fff', fontSize: '13px',
            fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
          }}
        >
          <UserPlus size={15} /> Crea utente
        </button>
      </div>

      <form onSubmit={handleSearch} style={{ marginBottom: '16px' }}>
        <div style={{ position: 'relative', maxWidth: '320px' }}>
          <Search size={15} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6c6c6d' }} />
          <input
            type="text"
            placeholder="Cerca per email..."
            value={emailFilter}
            onChange={e => setEmailFilter(e.target.value)}
            style={{
              width: '100%', padding: '8px 12px 8px 36px', borderRadius: '8px',
              border: '1px solid rgba(0,0,0,0.1)', background: '#ffffff',
              fontSize: '13px', boxSizing: 'border-box', outline: 'none',
              fontFamily: "'DM Sans', sans-serif",
            }}
          />
        </div>
      </form>

      {lastWarning && (
        <div
          role="status"
          style={{
            marginBottom: '16px', padding: '12px 16px', borderRadius: '8px',
            background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)',
            color: '#B45309', fontSize: '13px', display: 'flex',
            alignItems: 'center', justifyContent: 'space-between', gap: '12px',
          }}
        >
          <span>Avviso: {lastWarning}</span>
          <button
            type="button"
            onClick={() => setLastWarning('')}
            aria-label="Chiudi avviso"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#B45309', fontSize: '16px', lineHeight: 1,
              padding: '0 4px', fontFamily: "'DM Sans', sans-serif",
            }}
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: '8px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', color: '#EF4444', fontSize: '13px' }}>
          {error}
        </div>
      )}

      <div style={{ background: '#ffffff', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <AdminTable
          columns={columns}
          rows={users}
          loading={loading}
          emptyMessage="Nessun utente trovato"
        />
      </div>

      <AdminPagination total={total} limit={LIMIT} offset={offset} onChange={setOffset} />

      <TenantCreateUserModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(warning) => {
          if (warning) setLastWarning(warning);
          loadUsers();
        }}
      />

      <ConfirmDialog
        open={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={confirmRemove}
        title="Rimuovi utente"
        message={`Rimuovere ${removeTarget?.email} dal tenant ${activeTenant.display_name}?`}
        confirmLabel="Rimuovi"
        variant="danger"
      />
    </div>
  );
}
